import React, { useMemo, useState, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { ButtonGroup } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import AvatarModal from '@atlassian/jira-project-settings-apps-avatar-modal/src/index.tsx';
import type { Avatar } from '@atlassian/jira-project-settings-apps-avatar-modal/src/model/types.tsx';
import type { NewMainHeaderProps } from '../../types.tsx';
import type { IssueLayoutOwner } from '../../types/issue/issue-layout-owner.tsx';
import { AvatarIcon } from '../avatar-icon/index.tsx';
import { NameInlineInput } from '../name-inline-input/index.tsx';
import { JSD_IS_EMAIL_REQUEST_TYPE } from '../../constants.tsx';

export type PropsWithMessages<T extends IssueLayoutOwner> = NewMainHeaderProps<T> & {
	issueType: T;
	messages: {
		// avatar
		avatarAccessibleAlt: MessageDescriptor;
		avatarAriaLabel: MessageDescriptor;
		avatarAlt: MessageDescriptor;
		editAvatar: MessageDescriptor;
		// validation
		errorMessageIssueTypeNameTooShort: MessageDescriptor;
		errorMessageIssueTypeNameTooLong: MessageDescriptor;
		errorMessageIssueTypeNameAlreadyExists: MessageDescriptor;
		errorMessageIssueTypeHelpTextTooLong: MessageDescriptor;
		errorMessageIssueTypeDescriptionTooLong: MessageDescriptor;
		// Name inline input
		editNameButtonLabel: MessageDescriptor;
		editNameInputLabel: MessageDescriptor;
	};
};

export const MainHeaderTmp = <T extends IssueLayoutOwner>(props: PropsWithMessages<T>) => {
	const {
		issueType,
		extraActions,
		onUpdateIssueLayoutOwner,
		issueTypeResetCount,
		IssueTypeRestriction,
		EditWorkflowButton,
		isIssueRestrictionEnabled,
		shouldShowWorkflowEditor,
		project,
		messages,
	} = props;

	const { properties } = issueType.data;
	const isEmailRequestType = properties && properties[JSD_IS_EMAIL_REQUEST_TYPE];
	const [isAvatarModalOpen, setIsAvatarModalOpen] = useState<boolean>(false);
	const { name, iconUrl, avatarId, id: issueTypeId } = issueType.data;

	const openAvatarModal = () => setIsAvatarModalOpen(true);
	const closeAvatarModal = () => setIsAvatarModalOpen(false);
	const onAvatarSelected = (avatar: Avatar) => {
		onUpdateIssueLayoutOwner({
			...issueType,
			data: {
				...issueType.data,
				avatarId: String(avatar.id),
				iconUrl: avatar.uri,
			},
		});
		setIsAvatarModalOpen(false);
	};

	const onNameChange = (newName: string) => {
		onUpdateIssueLayoutOwner({
			...issueType,
			data: {
				...issueType.data,
				name: newName,
			},
		});
	};

	const allActions = [];
	if (shouldShowWorkflowEditor) {
		allActions.push(<EditWorkflowButton key="edit-workflow" issueTypeId={issueTypeId} />);
	}
	if (isIssueRestrictionEnabled) {
		allActions.push(<IssueTypeRestriction key="restriction" issueTypeName={name} />);
	}
	if (extraActions) {
		allActions.push(...extraActions);
	}

	const nameInlineInputMessages = useMemo(
		() => ({
			editNameButtonLabel: messages.editNameButtonLabel,
			editNameInputLabel: fg('jsm_rtc_document_title_a11y')
				? messages.editNameInputLabel
				: undefined,
		}),
		[messages.editNameButtonLabel, messages.editNameInputLabel],
	);

	return (
		<Container>
			<div>
				<AvatarIcon
					onEdit={openAvatarModal}
					avatarUri={iconUrl || ''}
					label={name}
					messages={messages}
				/>
			</div>
			<AvatarModal
				isOpen={isAvatarModalOpen}
				onAvatarSelected={onAvatarSelected}
				onDismiss={closeAvatarModal}
				selectedAvatar={{
					id: Number(avatarId ?? 0),
					uri: iconUrl || '',
				}}
				projectId={project.projectId}
			/>
			<Header>
				<PageHeader
					truncateTitle
					disableTitleStyles
					actions={
						<Box xcss={buttonWrapperStyles}>
							<ButtonGroup>{allActions}</ButtonGroup>
						</Box>
					}
				>
					<NameInlineInput
						issueTypeId={issueTypeId}
						projectId={project.projectId}
						canBeEdited={!isEmailRequestType}
						issueTypeResetCount={issueTypeResetCount}
						value={name}
						validationMessages={messages}
						onChange={onNameChange}
						messages={nameInlineInputMessages}
					/>
				</PageHeader>
			</Header>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div<{
	children?: ReactNode;
}>({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div<{
	children?: ReactNode;
}>({
	flexGrow: 1,
	width: 0,
	/* The AK PageHeader component has built in margins that don't match our desired layout for the SWIFT page so let's override it. */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		margin: 0,
	},
});

const buttonWrapperStyles = xcss({
	marginTop: 'space.100',
});
